import { FC, Fragment, useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { BvIcon } from 'bevi-icon';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { SimulationBankService } from '../../../../../services/SimulationBank/SimulationBankService';
import FormatMoney from '../../../../../utils/FormatMoney';

interface StepResultadoProps {
    onFinalizar: () => void;
    onBack?: () => void;
}

const isCrefazFormalizacaoAtrasada = (mensagem?: string): boolean => {
    if (!mensagem) return false;
    const texto = mensagem.toLowerCase();
    return texto.includes('crefaz') && texto.includes('fluxo') && texto.includes('demorando');
};

const StepResultado: FC<StepResultadoProps> = ({ onFinalizar, onBack }) => {
    const { propostaResultado, ofertaSelecionada, screeningId } = useSimulationBank();
    const [formalizationLinks, setFormalizationLinks] = useState<Record<string, string>>({});
    const [copiedLink, setCopiedLink] = useState<string | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const linksWithDataRef = useRef<Set<string>>(new Set());
    const service = new SimulationBankService();

    const handleCopyLink = (link: string) => {
        navigator.clipboard.writeText(link);
        setCopiedLink(link);
        setTimeout(() => setCopiedLink(null), 2000);
    };

    useEffect(() => {
        if (propostaResultado?.status !== 'EFETIVADO' || !screeningId || !('resultados' in propostaResultado)) return;

        const pollFormalizationLinks = async () => {
            for (const resultado of propostaResultado.resultados) {
                if (!resultado.codigo_proposta) continue;

                const hasLinkAlready = resultado.assinatura?.link || linksWithDataRef.current.has(resultado.codigo_proposta);
                if (!hasLinkAlready) {
                    const [response, error] = await service.getFormalizationLink(screeningId, resultado.codigo_proposta);
                    if (!error && response?.data?.formalization_link) {
                        linksWithDataRef.current.add(resultado.codigo_proposta);
                        setFormalizationLinks((prev) => ({
                            ...prev,
                            [resultado.codigo_proposta]: response.data.formalization_link,
                        }));
                    }
                }
            }

            // Para o polling se todos os links foram obtidos
            const allHaveLinks = propostaResultado.resultados.every((r) =>
                r.assinatura?.link || linksWithDataRef.current.has(r.codigo_proposta)
            );

            if (allHaveLinks && pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };

        pollFormalizationLinks();
        pollingRef.current = setInterval(pollFormalizationLinks, 30000);

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [propostaResultado, screeningId]);

    if (!propostaResultado) return null;

    const crefazFormalizacaoAtrasada = propostaResultado.status === 'erro'
        && isCrefazFormalizacaoAtrasada(propostaResultado.mensagem);

    if (propostaResultado.status === 'erro' && !crefazFormalizacaoAtrasada) {
        return (
            <div className="step-results-v2 step-results-v2--error">
                <div className="resultado-sucesso">
                    <div className="resultado-sucesso__icon">
                        <BvIcon name="cancel" width={100} height={100} color="#7779BB" />
                    </div>
                    <p className="step-results-v2__lead resultado-sucesso__title">Não foi possível efetivar</p>
                </div>

                <div className="sim-resultado__card">
                    <div className="sim-resultado__card-header">
                        <p className="sim-resultado__card-label">Valor solicitado</p>
                        <p className="sim-resultado__card-name">
                            {ofertaSelecionada?.valor_solicitado != null ? FormatMoney(ofertaSelecionada.valor_solicitado) : '—'}
                        </p>
                    </div>

                    <div className="sim-resultado__card-divider">
                        <div className="sim-resultado__field">
                            <span className="sim-resultado__field-label">Financeira</span>
                            <span className="sim-resultado__field-value">{ofertaSelecionada?.financeira}</span>
                        </div>
                        <div className="sim-resultado__field">
                            <span className="sim-resultado__field-label">Proposta</span>
                            <span className="sim-resultado__field-value">—</span>
                        </div>
                        <div className="sim-resultado__field">
                            <span className="sim-resultado__field-label">Parcelas</span>
                            <span className="sim-resultado__field-value">{ofertaSelecionada?.qtde_parcelas ? `${ofertaSelecionada.qtde_parcelas}x` : '—'}</span>
                        </div>
                    </div>
                </div>

                <div className="sim-resultado__error-message">
                    <p className="sim-resultado__error-message-primary">
                        <BvIcon name="emergencyHome" variant="dark" height={20} width={20} /> {propostaResultado.mensagem}
                    </p>
                </div>

                <div className="resultado-sucesso__actions">
                    {onBack && (
                        <button className="btn-ghost-sim" onClick={onBack}>
                            Voltar ao Formulário
                        </button>
                    )}
                    <button className="btn-simulation-primary" onClick={onFinalizar}>
                        Finalizar
                    </button>
                </div>
            </div>
        );
    }

    if ((propostaResultado.status === 'EFETIVADO' && 'resultados' in propostaResultado) || crefazFormalizacaoAtrasada) {
        const resultados = crefazFormalizacaoAtrasada
            ? [{ financeira: ofertaSelecionada?.financeira, codigo_proposta: '' } as any]
            : (propostaResultado as any).resultados;

        return (
            <div className="sim-resultado sim-resultado--final">
                <div className="sim-resultado__container">
                    <div className="sim-resultado__icon-wrapper">
                        <div className="sim-resultado__icon-ring">
                            <div className="sim-resultado__icon-circle">
                                <BvIcon name="verified" width={70} height={68} color="#222343" />
                            </div>
                        </div>
                    </div>
                    <h2 className="sim-resultado__title">Proposta digitada com sucesso</h2>
                    <p className="sim-resultado__subtitle">Sua proposta foi digitada no sistema do banco.</p>
                </div>

                {resultados.map((resultado: any, idx: number) => (
                    <Fragment key={resultado.codigo_proposta || idx}>
                    <div className="sim-resultado__card">
                        <div className="sim-resultado__card-header">
                            <p className="sim-resultado__card-label">Valor solicitado</p>
                            <p className="sim-resultado__card-name">
                                {ofertaSelecionada?.valor_solicitado != null ? FormatMoney(ofertaSelecionada.valor_solicitado) : '—'}
                            </p>
                        </div>

                        <div className="sim-resultado__card-divider">
                            <div className="sim-resultado__field">
                                <span className="sim-resultado__field-label">Financeira</span>
                                <span className="sim-resultado__field-value">{resultado.financeira ?? '—'}</span>
                            </div>
                            <div className="sim-resultado__field">
                                <span className="sim-resultado__field-label">Proposta</span>
                                <span className="sim-resultado__field-value">{resultado.codigo_proposta || '—'}</span>
                            </div>
                            <div className="sim-resultado__field">
                                <span className="sim-resultado__field-label">Parcelas</span>
                                <span className="sim-resultado__field-value">
                                    {ofertaSelecionada?.qtde_parcelas ? `${ofertaSelecionada.qtde_parcelas}x` : '—'}
                                </span>
                            </div>
                            <div className="sim-resultado__field">
                                <span className="sim-resultado__field-label">Efetivado em</span>
                                <span className="sim-resultado__field-value">
                                    {!crefazFormalizacaoAtrasada && 'data_efetivacao' in propostaResultado
                                        ? moment(propostaResultado.data_efetivacao).format('DD/MM/YYYY HH:mm')
                                        : '—'}
                                </span>
                            </div>
                        </div>

                        {resultado.codigo_proposta && (() => {
                            const link = resultado.assinatura?.link || formalizationLinks[resultado.codigo_proposta] || '';
                            return (
                                <div className="sim-resultado__signature">
                                    <p className="sim-resultado__signature-label">Formalização Digital</p>
                                    {link ? (
                                        <>
                                            <p className="sim-resultado__signature-intro">Enviar link da formalização digital para o cliente:</p>
                                            <div className="sim-resultado__signature-link-container">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14} className="sim-resultado__signature-link-icon">
                                                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                                                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                                                </svg>
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="sim-resultado__signature-link"
                                                >
                                                    {link}
                                                </a>
                                                <button className="sim-resultado__signature-link-copy" onClick={() => handleCopyLink(link)}>
                                                    {copiedLink === link ? 'Copiado!' : 'Copiar link'}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="sim-resultado__signature-link-container">
                                            <span className="sim-resultado__signature-intro sim-resultado__signature-intro--loading">
                                                Gerando link da formalização digital
                                            </span>
                                            <span className="sim-resultado__signature-spinner" />
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="sim-resultado__info-card">
                        <p>
                            Para acompanhar sua proposta ou acessar o link de formalização caso não tenha sido gerado                acima, é só buscar no agente <strong>Consulta de Proposta</strong> aqui
                            no BevIAjuda
                        </p>
                    </div>
                    </Fragment>
                ))}

                <button
                    className="btn-simulation-primary sim-resultado__button"
                    onClick={onFinalizar}
                >
                    Finalizar
                </button>
            </div>
        );
    }

    return null;
};

export default StepResultado;
